import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import DeleteIcon from '../../../assets/icons/delete.svg?react';
import EditIcon from '../../../assets/icons/edit.svg?react';
import { useReportTemplateList } from '../../../hooks/queries/reportQueries';

import dayjs from 'dayjs';
import PlusIcon from '../../../assets/icons/plus.svg?react';
import ReportTemplateAppendModal from './ReportTemplateAppendModal';
import ReportTemplateUpdateModal from './ReportTemplateUpdateModal';
import ReportTemplateDeleteModal from './ReportTemplateDeleteModal';
import { SkeletonTable } from '../../../components/Skeleton';
import Pagination from '../../../components/Pagination/Pagination';
const ReportTemplate = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const pageSize = parseInt(searchParams.get('pageSize')) || 10;
  const sortBy = searchParams.get('sortBy') || 'CreatedDate';
  const sortOrder = searchParams.get('sortOrder') || 'desc';
  const [keywordInput, setKeywordInput] = useState(''); // 입력 중인 키워드 (로컬 state)
  const keyword = searchParams.get('Keyword') || '';
  
  // keywordInput을 URL의 keyword와 동기화 (초기값 설정)
  useEffect(() => {
    if (keyword) {
      setKeywordInput(keyword);
    } else {
      setKeywordInput('');
    }
  }, [keyword]);
  
  // Query Params Memoization
  const queryParams = useMemo(() => {
    const params = {
      Page: currentPage,
      PageSize: pageSize,
      OrderBy: sortBy,
      OrderDirection: sortOrder
    };
    
    // Keyword 파라미터 추가
    if (keyword) {
      params.Keyword = keyword;
    }
    
    return params;
  }, [currentPage, pageSize, sortBy, sortOrder, keyword]);
  
  const { data: reportTemplates, isLoading, error, refetch } = useReportTemplateList(queryParams);
  const totalPages = reportTemplates?.Total ? Math.ceil(reportTemplates.Total / pageSize) : 1;
  const [isAppendModalOpen, setIsAppendModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [target, setTarget] = useState(null);
  
  // 키워드 입력 핸들러
  const handleKeywordChange = (e) => {
    setKeywordInput(e.target.value);
  };
  
  // 키워드 검색 핸들러 (Enter 키)
  const handleKeywordKeyDown = (e) => {
    if (e.key === 'Enter') {
      const value = e.target.value.trim();
      if (value.length >= 2) {
        setSearchParams(prev => {
          const newParams = new URLSearchParams(prev);
          newParams.set('Keyword', value);
          newParams.set('page', '1'); // 검색 시 첫 페이지로
          return newParams;
        });
      } else if (value.length === 0) {
        // 빈 값이면 Keyword 파라미터 제거
        setSearchParams(prev => {
          const newParams = new URLSearchParams(prev);
          newParams.delete('Keyword');
          newParams.set('page', '1');
          return newParams;
        });
      }
    }
  };
  const handleCloseAppendModal = () => {
    setIsAppendModalOpen(false);
    refetch();
  };
  const handleDeleteSuccess = () => {
    setIsDeleteModalOpen(false);
    refetch();
  };
  const handleDelete = (target) => {
    setTarget(target);
    setIsDeleteModalOpen(true);
  };
  const handleUpdateSuccess = () => {
    setIsUpdateModalOpen(false);
    refetch();
  };
  const handleUpdate = (target) => {
    setTarget(target);
    setIsUpdateModalOpen(true);
  };
  const handlePageChange = (page) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('page', page.toString());
      return newParams;
    });
  };
  return (
    <>
      <div className="flex justify-between align-center mb-10">
        <div className="flex w-half">
          <input 
            type="text" 
            placeholder="키워드 검색 (2자 이상) - 템플릿명, 버전" 
            className="input-primary flex-1 text-b2-400" 
            value={keywordInput}
            onChange={handleKeywordChange}
            onKeyDown={handleKeywordKeyDown}
          />
        </div>
        <div className="flex justify-end">
          <button className="btn big-btn btn-primary flex align-center gap-3" onClick={() => setIsAppendModalOpen(true)}>
            <PlusIcon color='var(--white)'/>
            템플릿 생성
          </button>
        </div>
      </div>
      <div className="primary-table">
        <table>
          <thead>
            <tr>
              <th>템플릿명</th>
              <th>버전</th>
              <th>생성일</th>
              <th>설명</th>
              <th width="100px"></th>
            </tr>
          </thead>
          <tbody>
            {reportTemplates?.Items?.length === 0 ? (
              <tr>
                <td colSpan="8" className="no-data">
                  <p className="text-b3-700 text-gray-500">템플릿 데이터가 없습니다.</p>
                </td>
              </tr>
            ) : (
              reportTemplates?.Items?.map(reportTemplate => (
              <tr key={reportTemplate.ReportTemplateInfoSysId}>
                <td className="report-name">{reportTemplate.TemplateName}</td>
                <td className="report-type">{reportTemplate.Version}</td>
                <td className="report-date">{reportTemplate.CreatedDate ? dayjs(reportTemplate.CreatedDate).format('YYYY-MM-DD HH:mm:ss') : '-'}</td>
                <td className="report-template">{reportTemplate.Description}</td>
                <td>
                  <div className="flex gap-5 justify-center">
                    <button className="btn icon-btn" onClick={() => handleUpdate(reportTemplate)}><EditIcon color='var(--gray-600)'/></button>
                    <button className="btn icon-btn" onClick={() => handleDelete(reportTemplate)}><DeleteIcon color='var(--gray-600)'/></button>
                  </div>
                </td>
              </tr>
            ))
            )}
          </tbody>
        </table>
        <div className="pagination-wrapper">
          <Pagination
          currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            />
        </div>

      </div>
      {isAppendModalOpen && (
        <ReportTemplateAppendModal isOpen={isAppendModalOpen} onClose={handleCloseAppendModal} />
      )}
      {isDeleteModalOpen && (
        <ReportTemplateDeleteModal onClose={() => setIsDeleteModalOpen(false)} target={target} onDeleteSuccess={handleDeleteSuccess} />
      )}
      {isUpdateModalOpen && (
        <ReportTemplateUpdateModal onClose={() => setIsUpdateModalOpen(false)} target={target} onUpdateSuccess={handleUpdateSuccess} />
      )}
    </>
  )
}

export default ReportTemplate